import type { InstanceOptions, ModalOptions, ModalInterface } from 'flowbite';
import { Modal } from 'flowbite';
import { breakPoints } from '../../../config/saas-structure';

interface ModalInterfaceExtended extends ModalInterface {
    _instanceId: string; // for some reason this is private on ModalInterface
}

declare global {
    interface Window {
        VidyardV4?: {
            api?: {
                getPlayersByUUID(uuid: string): any[];
            };
        };
    }
}

// Using the following Flowbite data attributes for interactions seems to break modal functionality
// (or at least doesn't take the ModalOptions into account)
// when using Flowbite JS API: 
// data-modal-target, data-modal-toggle, data-modal-show

// Using custom data attributes with event listers instead, to call modal methods: 
// data-ot-modal-show, data-ot-modal-hide

export const otModals = {
    modals: [] as ModalInterfaceExtended[],
    init() {
        const $modals: NodeListOf<HTMLElement> = document.querySelectorAll('[data-ot-modal]');

        $modals.forEach($modal => {
            const modal = this.configureModal($modal);
            this.modals.push(modal);
            this.bindUIEvents(modal);
            this.openModalViaURLParam(modal);
        });

        document.addEventListener('click', (e) => {
            // Handle dynamically added triggers (such as ing language alert)
            if (e.target instanceof Element && e.target.hasAttribute('data-ot-modal-show')) {
                const targetID = (e.target.getAttribute('data-ot-modal-show') || '').replace(/^#/, '');

                const modal = this.modals.find(modal => modal._instanceId === 'modal-instance-' + targetID);

                if (modal) {
                    modal.show();
                }
            }
        });
    },
    configureModal($modal: HTMLElement) {
        const options: ModalOptions = {
            backdropClasses: 'fixed inset-0 z-40 bg-page-modal',
            onHide: () => this.stopVideo(modal)
        };

        const instanceOptions: InstanceOptions = {
            id: 'modal-instance-' + $modal.id,
            override: true
        };

        const modal: ModalInterfaceExtended = new Modal($modal, options, instanceOptions);
        return modal;
    },
    bindUIEvents(modal: ModalInterfaceExtended ) {
        const $modal = modal._targetEl;
        const currentModalId = $modal?.id;
        const $modalTriggers: NodeListOf<HTMLButtonElement> = document.querySelectorAll(`[data-ot-modal-show="${currentModalId}"]`);

        // Open modal via button click
        $modalTriggers.forEach($modalTrigger => {
            $modalTrigger.addEventListener('click', () => {
                modal.show();
            })
        });

        // Close modal via modal's close button
        const closeButton = $modal?.querySelector('[data-ot-modal-hide]');
        closeButton?.addEventListener('click', () => {
            modal.hide();
        });

        window.addEventListener('resize', () => {
            this.hideAtBreakpoints(modal);
        });

        this.hideAtBreakpoints(modal);
    },
    async stopVideo(modal: ModalInterfaceExtended) {
        const vidyardUUID = modal._targetEl?.children[0].querySelector('[uuid]')?.getAttribute('uuid');
        const $youtubeIframe: any = modal._targetEl?.children[0].querySelector(`[data-ot-youtube-embed]`);

        if (vidyardUUID) {
            const vidyard = vidyardUUID && window.VidyardV4?.api?.getPlayersByUUID(vidyardUUID) || [];
            vidyard.length && vidyard.forEach((video: any) => video.pause());
        }

        if ($youtubeIframe?.querySelector('iframe')) {
            const player = await $youtubeIframe?.querySelector('lite-youtube')?.getYTPlayer();
            player.pauseVideo();
        }
    },
    hideAtBreakpoints(modal: ModalInterfaceExtended) {
        const $modal = modal._targetEl;

        if ($modal?.dataset.otModalBreakpointMin || $modal?.dataset.otModalBreakpointMax ) {
            const minBpName = $modal?.dataset.otModalBreakpointMin;
            const minBpValue = minBpName && (breakPoints[minBpName].minWidth - 1);
            (minBpValue && document.documentElement.clientWidth <= minBpValue) && modal.hide();

            const maxBpName = $modal?.dataset.otModalBreakpointMax;
            const maxBpValue = maxBpName && breakPoints[maxBpName].minWidth;
            (maxBpValue && document.documentElement.clientWidth >= maxBpValue) && modal.hide();

        }
    },
    openModalViaURLParam(modal: ModalInterfaceExtended) {
        const urlParams = new URLSearchParams(window.location.search);
        const urlParam = urlParams.get('modal');
        if (urlParam !== modal._targetEl?.id) return;
        modal.show();
    }
}
